import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  ElementRef,
  QueryList,
  ViewChild,
  ViewChildren,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { GetPassengerDto } from '../../../../api/gen/api';
import {
  Subject,
  catchError,
  debounceTime,
  distinctUntilChanged,
  map,
  of,
  switchMap,
  tap,
} from 'rxjs';
import { SvgIconComponent } from 'angular-svg-icon';
import { SidebarNavigationItem } from '../../models/sidebar-navigation-item.model';
import { PassengerService } from '../../services/api/passenger.service';
import { CommandPaletteService } from '../../services/command-palette.service';

@Component({
  selector: 'app-command-palette',
  imports: [CommonModule, FormsModule, SvgIconComponent],
  templateUrl: './command-palette.component.html',
  styleUrl: './command-palette.component.scss',
})
export class CommandPaletteComponent {
  commandPalette = inject(CommandPaletteService);
  private readonly router = inject(Router);
  private readonly passengerService = inject(PassengerService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly queryChanges$ = new Subject<string>();
  private passengersCache: GetPassengerDto[] | null = null;

  @ViewChild('commandInput') commandInput?: ElementRef<HTMLInputElement>;
  @ViewChildren('resultItem') resultItems?: QueryList<ElementRef<HTMLButtonElement>>;

  constructor() {
    this.queryChanges$
      .pipe(
        debounceTime(250),
        distinctUntilChanged(),
        switchMap((query) => this.loadPassengerItems(query)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((items) => {
        this.commandPalette.setDynamicPassengerItems(items);
        this.scrollSelectedItemIntoView();
      });
  }

  focusInput() {
    setTimeout(() => {
      this.commandInput?.nativeElement.focus();
      this.commandInput?.nativeElement.select();
    }, 0);
  }

  onQueryChange(value: string) {
    this.commandPalette.updateQuery(value);
    this.queryChanges$.next(value);
    this.scrollSelectedItemIntoView();
  }

  onOverlayClick() {
    this.commandPalette.close();
  }

  onListKeydown(event: KeyboardEvent) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.commandPalette.moveSelection(1);
        this.scrollSelectedItemIntoView();
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.commandPalette.moveSelection(-1);
        this.scrollSelectedItemIntoView();
        break;
      case 'Enter':
        event.preventDefault();
        this.navigateTo(this.commandPalette.activeItem);
        break;
      case 'Escape':
        event.preventDefault();
        this.commandPalette.close();
        break;
      default:
        break;
    }
  }

  navigateTo(item: SidebarNavigationItem | undefined) {
    if (!item) {
      return;
    }

    this.router.navigateByUrl(item.route);
    this.commandPalette.close();
  }

  private loadPassengerItems(query: string) {
    const normalizedQuery = query.trim().toLowerCase();
    if (!normalizedQuery) {
      this.commandPalette.setPassengerSearchLoading(false);
      return of([] as SidebarNavigationItem[]);
    }

    this.commandPalette.setPassengerSearchLoading(true);

    const source$ = this.passengersCache
      ? of(this.passengersCache)
      : this.passengerService.getAllPassengers().pipe(
          tap((passengers) => {
            this.passengersCache = passengers;
          }),
        );

    return source$.pipe(
      map((passengers) => this.buildPassengerItems(passengers, normalizedQuery)),
      catchError(() => of([] as SidebarNavigationItem[])),
      tap(() => {
        this.commandPalette.setPassengerSearchLoading(false);
      }),
    );
  }

  private buildPassengerItems(
    passengers: GetPassengerDto[],
    normalizedQuery: string,
  ): SidebarNavigationItem[] {
    const terms = normalizedQuery.split(' ').filter((term) => term.length > 0);

    return passengers
      .filter((passenger) => {
        const firstName = passenger.firstName?.toLowerCase() ?? '';
        const lastName = passenger.lastName?.toLowerCase() ?? '';
        const fullName = `${firstName} ${lastName}`.trim();
        const reverseName = `${lastName} ${firstName}`.trim();
        const searchable = [fullName, reverseName, firstName, lastName]
          .filter((value) => value.length > 0)
          .join(' ');

        return terms.every((term) => searchable.includes(term));
      })
      .slice(0, 20)
      .sort((a, b) => {
        const aName = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim();
        const bName = `${b.firstName ?? ''} ${b.lastName ?? ''}`.trim();
        return aName.localeCompare(bName, 'de', { sensitivity: 'base' });
      })
      .flatMap((passenger) => {
        const firstName = passenger.firstName ?? '';
        const lastName = passenger.lastName ?? '';
        const fullName = `${firstName} ${lastName}`.trim() || 'Unbekannter Fahrgast';
        const encodedFullName = encodeURIComponent(fullName);

        return [
          {
            icon: 'group',
            label: `Fahrgast bearbeiten: ${fullName}`,
            route: `/passenger/editPassenger/${passenger.id}`,
            aliases: [fullName, `fahrgast bearbeiten ${fullName}`],
            hideRoute: true,
            isEdit: true,
          },
          {
            icon: 'calendar',
            label: `Fahrten von Fahrgast ${fullName} im Kalender anzeigen`,
            route: `/passenger/calendar?passengerId=${passenger.id}`,
            aliases: [
              fullName,
              `kalender ${fullName}`,
              `fahrten ${fullName}`,
            ],
            hideRoute: true,
            isPassengerCalendarAction: true,
          },
          {
            icon: 'route',
            label: `Transportauftraege von Fahrgast ${fullName} anzeigen`,
            route: `/trip/search/${encodedFullName}`,
            aliases: [
              fullName,
              `transportauftraege ${fullName}`,
              `transportauftraege von fahrgast ${fullName}`,
            ],
            hideRoute: true,
            isPassengerTripAction: true,
          },
        ];
      });
  }

  private scrollSelectedItemIntoView() {
    setTimeout(() => {
      const selected = this.resultItems?.get(this.commandPalette.selectedIndex);
      selected?.nativeElement.scrollIntoView({ block: 'nearest' });
    }, 0);
  }
}
